/* When a booking can move by trading places, and how a walk-in is measured
 * once it has.
 *
 * The board writes a booking off — counts it as not in a technician's way —
 * when a colleague is free to take it. "Free" there meant free of their own
 * book, so on a full afternoon nobody ever was, every booking read as a wall,
 * and the technician furthest behind in the rotation was the one passed over:
 * free now, with a client due in twenty minutes that nobody else could take.
 *
 * But the colleague is often free for it except for one booking of their own
 * that the first technician could take instead. Lauriann idle at 2:43 with a
 * 3:00 eighty-minute pedicure; Thu idle too, with a 3:50 of his own. Neither
 * booking has anybody free for it, and yet the walk-in can go to Lauriann: the
 * 3:00 goes to Thu, and Thu's 3:50 comes back to her once the walk-in is done.
 * That is a two-way swap, and it is the only depth this looks to. Longer
 * chains exist on paper; at a front desk they are not a plan anybody carries
 * out.
 *
 * A swap is not free, either. The booking that comes back is a new wall in
 * the swapping technician's afternoon, and for a long walk-in it can sit in
 * exactly the wrong place — keeping the 3:00 would have freed her at 4:20,
 * taking the 3:50 back frees her at 4:40. So a walk-in is measured both ways
 * and the earlier start wins, with a tie going to the book as it stands: a
 * plan that moves nothing beats one that moves two bookings for the same
 * answer. See `bestStart`.
 *
 * Pure: blocks and candidates arrive as plain objects, and the questions only
 * store.js can answer — is this colleague clear of squares and shift for the
 * window, could the first technician take that booking — arrive already
 * answered or as a plain function.
 */

function overlaps(a, b) { return a.from < b.to && a.to > b.from; }

/* May this technician do this booking at all — the same question for both
 * halves of a swap, the booking going out and the one coming back.
 *
 * Two things, both read off the booking as it really is rather than as its
 * menu name says:
 *
 *   mappedServiceId   the turn type after its options have had their say —
 *                     a pedicure booked with acrylic toes is acrylic work.
 *                     Null means the board cannot classify it, and work it
 *                     cannot classify is work it cannot vouch for anybody
 *                     doing, so nobody may take it in a trade.
 *   allowedStaffIds   who its options limit it to (a design, say), or null
 *                     when nothing does. Several limiting options arrive
 *                     already intersected.
 *
 * A swap is the board volunteering somebody for a client's booking; handing
 * a detailed design to a technician who does not do designs is the one kind
 * of wrong answer the desk has no reason to suspect.
 */
export function mayTake(staff, mappedServiceId, allowedStaffIds) {
  if (!staff || !mappedServiceId) return false;
  if ((staff.serviceIds || []).indexOf(mappedServiceId) === -1) return false;
  return !allowedStaffIds || allowedStaffIds.indexOf(staff.id) !== -1;
}

/* A colleague who could take `block` if they handed back what of theirs is
 * in the way, or null.
 *
 *   block        { from, to } — the booking looking for a home.
 *   candidates   colleagues in the order the desk would reach for them, each
 *                { id, able, clear, spent, book }:
 *                  able   may do the booking at all — `mayTake`, with the
 *                         booking's options counted. Required.
 *                  clear  free of a running square, a bypass and the end of
 *                         their shift over the block's window — everything
 *                         but their own bookings.
 *                  spent  already promised to another of this technician's
 *                         bookings over the same stretch.
 *                  book   their own pinned bookings, { from, to, requested }.
 *   takeBack     (booking) -> could the first technician take this one of the
 *                colleague's instead — `mayTake` for her, and free for it.
 *                Asked only of bookings that overlap.
 *
 * Returns { to: candidate id, back: [their bookings that would come back] }.
 *
 * A colleague with nothing in the way is not a swap — that is an ordinary
 * stand-in, and the caller has already looked for one. Anything requested is
 * never handed back: a promise to a client is not traded to make room.
 */
/* Of everybody a swap would work with, the one whose returned booking starts
 * latest: the swap exists to make room in front of it, and a trade that hands
 * back a 3:00 for a 3:00 makes none. Ties keep the order given, which is the
 * order the desk would reach for them in. */
export function findSwap(block, candidates, takeBack) {
  var best = null, bestAt = -Infinity;
  for (var i = 0; i < (candidates || []).length; i++) {
    var swap = swapWith(block, candidates[i], takeBack);
    if (!swap) continue;
    var at = Math.min.apply(null, swap.back.map(function (b) { return b.from; }));
    if (at > bestAt) { best = swap; bestAt = at; }
  }
  return best;
}

function swapWith(block, c, takeBack) {
  // Asked here rather than trusted to the caller's list, and closed by
  // default: a candidate nobody said may do the work does not get it.
  if (!c.able || !c.clear || c.spent) return null;
  var inWay = (c.book || []).filter(function (b) { return overlaps(b, block); });
  if (!inWay.length) return null;
  if (inWay.some(function (b) { return b.requested; })) return null;
  // Everything in the way has to come back, and to one chair, so two of
  // them overlapping each other cannot both be taken.
  for (var j = 0; j < inWay.length; j++) {
    if (!takeBack(inWay[j])) return null;
    for (var k = 0; k < j; k++) {
      if (overlaps(inWay[j], inWay[k])) return null;
    }
  }
  return { to: c.id, back: inWay };
}

/* The soonest a walk-in needing `need` ms could start at or after `from`,
 * stepping past each block it would run into. Blocks in any order. */
export function firstGap(from, need, blocks) {
  var list = (blocks || []).slice().sort(function (a, b) { return a.from - b.from; });
  var at = from;
  for (var i = 0; i < list.length; i++) {
    if (list[i].to <= at) continue;          // done with before they are free
    if (list[i].from >= at + need) break;    // the gap in front of it is enough
    at = list[i].to;                         // no room: try the far side of it
  }
  return at;
}

/* Which version of the afternoon a walk-in is measured against.
 *
 *   kept      the blocks that stay whatever happens.
 *   swaps     [{ out, back: [...] }] — `out` leaves if the swap is made and
 *             everything in `back` arrives.
 *
 * All swaps or none: one or two a day is the realistic number, and a mix of
 * them is a plan nobody at the desk would hold in their head.
 *
 * Returns { at, swapped }. Equal starts go to the unswapped book.
 */
export function bestStart(from, need, kept, swaps) {
  var list = swaps || [];
  var outs = list.map(function (s) { return s.out; });
  var asIs = firstGap(from, need, (kept || []).concat(outs));
  if (!list.length) return { at: asIs, swapped: false };
  var backs = [];
  list.forEach(function (s) { backs = backs.concat(s.back); });
  var traded = firstGap(from, need, (kept || []).concat(backs));
  return traded < asIs ? { at: traded, swapped: true } : { at: asIs, swapped: false };
}

/* Why somebody ahead in the rotation is not the one named.
 *
 * The card naming Thu while Lauriann sits idle a full turn behind him reads as
 * the board getting the rotation wrong. It almost never is — she is spoken for
 * — but the card used to say nothing at all when somebody was free, so the
 * desk had no way to tell a good answer from a broken one.
 *
 *   passed   [{ name, kind, at, what }] in rotation order, kind being
 *              'request'  a client asked for them at `at`
 *              'booking'  a booking at `at` nobody else is free to take
 *              'shift'    their day ends at `at`, before this would
 *              'break'    a time block at `at`, before this would be done
 *            `at` already formatted as a clock; `what` the booking's line,
 *            or the block's name.
 *   named    who the card does name.
 */
export function passedOverNote(passed, named) {
  if (!passed || !passed.length) return '';
  var lines = passed.map(function (p) {
    if (p.kind === 'shift') {
      return p.name + ' is ahead in the rotation but is off at ' + p.at +
        ', before this would be finished.';
    }
    if (p.kind === 'break') {
      return p.name + ' is ahead in the rotation but has ' + (p.what || 'a break') +
        ' at ' + p.at + ', before this would be finished.';
    }
    var what = p.what ? ' ' + p.what : '';
    if (p.kind === 'request') {
      return p.name + ' is ahead in the rotation but has a ' + p.at + what +
        ' asked for by name, too soon to fit this in first.';
    }
    return p.name + ' is ahead in the rotation but has a ' + p.at + what +
      ' too soon to fit this in first, and nobody else is free to take it.';
  });
  return lines.join('\n') + (named ? '\nSo it goes to ' + named + '.' : '');
}
